import { useState, useEffect } from 'react';
import Mustache from 'mustache';
import { Card, CardBody, Badge, Spinner, Alert, Button, Select } from '@components/ui';
import { useTemplate } from '../hooks/useTemplate';
import { EmailTemplateModal } from './EmailTemplateModal';
import { CafeTemplateModal } from './CafeTemplateModal';
import { templateService } from '../services/templateService';
import type { CafeTemplate } from '../types/template.types';
import { useQueryClient } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useConsumption } from '@features/subscriptions/hooks';
import Swal from 'sweetalert2';

// No icons needed

export function TemplatesTab() {
  const { data, isLoading, error } = useTemplate();
  const queryClient = useQueryClient();

  // Subscription and plan validation
  const {
    canModifyTemplate,
    canModifyLogo,
    planLimits,
    isLoading: isLoadingPlan
  } = useConsumption();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCafeModalOpen, setIsCafeModalOpen] = useState(false);
  const [selectedCafe, setSelectedCafe] = useState<string>('');
  const [cafeTemplates, setCafeTemplates] = useState<CafeTemplate[]>([]);
  const [loadingCafeTemplates, setLoadingCafeTemplates] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Logo upload state
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Signature upload state
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [signatureUploadError, setSignatureUploadError] = useState<string | null>(null);
  const [signatureUploadSuccess, setSignatureUploadSuccess] = useState(false);

  // CAFE configuration fields
  const [cafeSignature, setCafeSignature] = useState('');
  const [cafeExtraHtml, setCafeExtraHtml] = useState('');
  const [cafeFooter, setCafeFooter] = useState('');

  // CAFE UI State
  const [cafeActiveTab, setCafeActiveTab] = useState<'view' | 'config'>('view');

  // UI State
  const [activeTab, setActiveTab] = useState<'general' | 'email' | 'colors' | 'cafe'>('general');
  const [showPreview, setShowPreview] = useState(false);
  const [showAdvancedColors, setShowAdvancedColors] = useState(false);

  // Preview state
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0); // Force refresh counter

  // Estado local para configuración general
  const [sendEmail, setSendEmail] = useState(false);
  const [description, setDescription] = useState('');

  // Estado local para configuración de email
  const [emailTo, setEmailTo] = useState('');
  const [emailTitle, setEmailTitle] = useState('');
  const [enableBanner, setEnableBanner] = useState(false);
  const [emailFrom, setEmailFrom] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [footer, setFooter] = useState('');

  // Campos de contenido del email (NO SE USAN - se envían como null)
  // const [greeting, setGreeting] = useState('');
  // const [message, setMessage] = useState('');
  // const [comment, setComment] = useState('');
  // const [thanks, setThanks] = useState('');

  // Estado local para los colores editables
  const [colors, setColors] = useState({
    // Colores básicos (usados para la vista previa)
    colorbackground: '',
    colorprimary: '',
    colortext: '',
    // Colores detallados
    backgroundBodyColor: '',
    backgroundBodyDecorationColor: '',
    backgroundFooterColor: '',
    backgroundHeaderColor: '',
    buttonRedirectEivoiceColor: '',
    buttonRedirectWebSiteColor: '',
    textButtonRedirectEivoiceColor: '',
    textButtonRedirectWebSiteColor: '',
    textPrimaryColor: '',
    textSegundaryColor: '',
  });

  // Handle logo upload
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (isLoadingPlan) {
      await Swal.fire({
        title: 'Cargando...',
        text: 'Verificando permisos del plan',
        icon: 'info',
        confirmButtonText: 'Entendido',
      });
      event.target.value = '';
      return;
    }

    if (planLimits && !canModifyLogo()) {
      const result = await Swal.fire({
        title: 'Función no disponible',
        text: 'La modificación del logo no está disponible en tu plan actual.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Entendido',
        cancelButtonText: 'Ver planes',
        confirmButtonColor: '#3085d6',
      });

      if (result.dismiss === Swal.DismissReason.cancel) {
        window.location.href = '/subscriptions/plans';
      }
      event.target.value = '';
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Solo se permiten archivos de imagen');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('El archivo debe ser menor a 5MB');
      return;
    }

    try {
      setIsUploadingLogo(true);
      setUploadError(null);
      setUploadSuccess(false);

      console.log('Uploading logo:', file.name, 'Size:', file.size);

      const response = await templateService.uploadLogo(file);

      console.log('Logo uploaded successfully:', response);

      setUploadSuccess(true);

      // Invalidate cache to reload template data with new logo
      queryClient.invalidateQueries({ queryKey: ['template'] });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setUploadError(
        err instanceof Error ? err.message : 'Error al subir el logo'
      );
    } finally {
      setIsUploadingLogo(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle logo delete
  const handleDeleteLogo = async () => {
    if (!data?.template) return;

    const result = await Swal.fire({
      title: '¿Eliminar logo?',
      text: 'El logo del comprobante será eliminado. Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });

    if (!result.isConfirmed) return;

    try {
      setIsUploadingLogo(true);
      setUploadError(null);

      // Update template with empty logo
      await templateService.updateTemplate(data.template._id, {
        ...data.template,
        emailTemplate: {
          ...data.template.emailTemplate,
          logo: '',
        },
      });

      // Invalidate cache to reload template data
      queryClient.invalidateQueries({ queryKey: ['template'] });

      await Swal.fire({
        title: '¡Eliminado!',
        text: 'El logo ha sido eliminado exitosamente.',
        icon: 'success',
        confirmButtonText: 'Entendido',
        timer: 2000,
      });
    } catch (err) {
      console.error('Error deleting logo:', err);
      setUploadError(
        err instanceof Error ? err.message : 'Error al eliminar el logo'
      );
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Handle signature upload
  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (isLoadingPlan) {
      await Swal.fire({
        title: 'Cargando...',
        text: 'Verificando permisos del plan',
        icon: 'info',
        confirmButtonText: 'Entendido',
      });
      event.target.value = '';
      return;
    }

    if (planLimits && !canModifyTemplate('cafe')) {
      const result = await Swal.fire({
        title: 'Función no disponible',
        text: 'La modificación de la firma CAFE no está disponible en tu plan actual.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Entendido',
        cancelButtonText: 'Ver planes',
        confirmButtonColor: '#3085d6',
      });

      if (result.dismiss === Swal.DismissReason.cancel) {
        window.location.href = '/subscriptions/plans';
      }
      event.target.value = '';
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSignatureUploadError('Solo se permiten archivos de imagen');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSignatureUploadError('El archivo debe ser menor a 5MB');
      return;
    }

    try {
      setIsUploadingSignature(true);
      setSignatureUploadError(null);
      setSignatureUploadSuccess(false);

      console.log('Uploading signature:', file.name, 'Size:', file.size);

      const response = await templateService.uploadSignature(file);

      console.log('Signature uploaded successfully:', response);

      setSignatureUploadSuccess(true);
      setCafeSignature(response.url);

      // Invalidate cache to reload template data with new signature
      queryClient.invalidateQueries({ queryKey: ['template'] });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSignatureUploadSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error uploading signature:', err);
      setSignatureUploadError(
        err instanceof Error ? err.message : 'Error al subir la firma'
      );
    } finally {
      setIsUploadingSignature(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Handle signature delete
  const handleDeleteSignature = async () => {
    if (!data?.template) return;

    const result = await Swal.fire({
      title: '¿Eliminar firma?',
      text: 'La firma del comprobante será eliminada. Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });

    if (!result.isConfirmed) return;

    try {
      setIsUploadingSignature(true);
      setSignatureUploadError(null);

      // Update template with empty signature
      await templateService.updateTemplate(data.template._id, {
        ...data.template,
        signature: '',
      });

      // Update local state
      setCafeSignature('');

      // Invalidate cache to reload template data
      queryClient.invalidateQueries({ queryKey: ['template'] });

      await Swal.fire({
        title: '¡Eliminado!',
        text: 'La firma ha sido eliminada exitosamente.',
        icon: 'success',
        confirmButtonText: 'Entendido',
        timer: 2000,
      });
    } catch (err) {
      console.error('Error deleting signature:', err);
      setSignatureUploadError(
        err instanceof Error ? err.message : 'Error al eliminar la firma'
      );
    } finally {
      setIsUploadingSignature(false);
    }
  };

  // Handle color changes
  const handleColorChange = (field: keyof typeof colors, value: string) => {
    setColors((prev) => {
      const newColors = { ...prev, [field]: value };

      // If changing a primary color, reset related advanced colors so the primary color takes effect
      if (field === 'colorprimary') {
        // Primary color affects: Header, Decorations, and some buttons
        newColors.backgroundHeaderColor = '';
        newColors.backgroundBodyDecorationColor = '';
        newColors.buttonRedirectEivoiceColor = '';
        newColors.textButtonRedirectWebSiteColor = '';
      } else if (field === 'colorbackground') {
        // Background color affects: Body and Footer backgrounds, and some buttons
        newColors.backgroundBodyColor = '';
        newColors.backgroundFooterColor = '';
        newColors.buttonRedirectWebSiteColor = '';
        newColors.textButtonRedirectEivoiceColor = '';
      } else if (field === 'colortext') {
        // Text color affects: Primary and Secondary text
        newColors.textPrimaryColor = '';
        newColors.textSegundaryColor = '';
      }

      return newColors;
    });

    // Auto-enable preview when user starts changing colors (only in colors tab)
    if (!showPreview && activeTab === 'colors' && data?.template?.emailTemplate?.emailTemplateId) {
      setShowPreview(true);
    }
  };

  // Auto-enable preview when user switches to Colors tab
  useEffect(() => {
    if (activeTab === 'colors' && !showPreview && data?.template?.emailTemplate?.emailTemplateId) {
      setShowPreview(true);
    }
  }, [activeTab, showPreview, data?.template?.emailTemplate?.emailTemplateId]);

  // Initialize all fields from template data
  useEffect(() => {
    if (data?.template) {
      // General configuration
      setSendEmail(data.template.sendEmail || false);
      setDescription(data.template.description || '');

      // Email configuration
      if (data.template.emailTemplate) {
        setEmailTo(data.template.emailTemplate.emailTo || '');
        setEmailTitle(data.template.emailTemplate.emailTitle || '');
        setEnableBanner(data.template.emailTemplate.enableBanner || false);
        setEmailFrom(data.template.emailTemplate.emailFrom || '');
        setEmailSubject(data.template.emailTemplate.emailSubject || '');
        setFooter(data.template.emailTemplate.footer || '');

        // Campos de contenido (NO SE USAN - siempre null)
        // setGreeting(data.template.emailTemplate.greeting || '');
        // setMessage(data.template.emailTemplate.message || '');
        // setComment(data.template.emailTemplate.comment || '');
        // setThanks(data.template.emailTemplate.thanks || '');

        // Colors
        setColors({
          // Colores básicos
          colorbackground: data.template.emailTemplate.colorbackground || '',
          colorprimary: data.template.emailTemplate.colorprimary || '',
          colortext: data.template.emailTemplate.colortext || '',
          // Colores detallados (con valores por defecto de la API)
          backgroundBodyColor: data.template.emailTemplate.backgroundBodyColor || '',
          backgroundBodyDecorationColor: data.template.emailTemplate.backgroundBodyDecorationColor || '',
          backgroundFooterColor: data.template.emailTemplate.backgroundFooterColor || '',
          backgroundHeaderColor: data.template.emailTemplate.backgroundHeaderColor || '',
          buttonRedirectEivoiceColor: data.template.emailTemplate.buttonRedirectEivoiceColor || '',
          buttonRedirectWebSiteColor: data.template.emailTemplate.buttonRedirectWebSiteColor || '',
          textButtonRedirectEivoiceColor: data.template.emailTemplate.textButtonRedirectEivoiceColor || '',
          textButtonRedirectWebSiteColor: data.template.emailTemplate.textButtonRedirectWebSiteColor || '',
          textPrimaryColor: data.template.emailTemplate.textPrimaryColor || '',
          textSegundaryColor: data.template.emailTemplate.textSegundaryColor || '',
        });
      }

      // CAFE configuration fields
      setCafeSignature(data.template.signature || '');
      setCafeExtraHtml(data.template.extraHtml || '');
      setCafeFooter(data.template.footer || '');
    }
  }, [data?.template]);

  // Load preview when showPreview is enabled (with debounce for color changes)
  // Solo en el tab de colores
  useEffect(() => {
    if (!showPreview || activeTab !== 'colors' || !data?.template?.emailTemplate?.emailTemplateId) {
      return;
    }

    const loadPreview = async () => {
      if (!data?.template?.emailTemplate?.emailTemplateId) {
        return;
      }

      try {
        setIsLoadingPreview(true);
        const templateIdToUse = data.template.emailTemplate.emailTemplateId;

        console.log('Loading preview for template:', templateIdToUse);
        const templateResponse = await templateService.getSendGridTemplate(templateIdToUse);

        if (!templateResponse || !templateResponse.templateHtml || !templateResponse.jsonTestModel) {
          console.error('Invalid template response:', templateResponse);
          return;
        }

        const testData = JSON.parse(templateResponse.jsonTestModel);

        // Use current colors or fall back to defaults - use getValidColor to handle empty strings
        const effectiveColors = {
          background: getValidColor(colors.colorbackground, testData.ColorFooter, '#dbe5ea'),
          primary: getValidColor(colors.colorprimary, testData.ColorPrimary, '#242b3d'),
          text: getValidColor(colors.colortext, testData.ColorHeader, '#126de5'),
        };

        const templateData = {
          ...testData,
          Logo: data.template?.emailTemplate?.logo || '',
          // Colores básicos
          ColorPrimary: effectiveColors.primary,
          ColorHeader: effectiveColors.text,
          ColorFooter: effectiveColors.background,
          // Colores avanzados (para Mustache variables) - use getValidColor to handle empty strings
          BackgroundHeaderColor: getValidColor(colors.backgroundHeaderColor, effectiveColors.primary),
          BackgroundBodyColor: getValidColor(colors.backgroundBodyColor, effectiveColors.background),
          BackgroundBodyDecorationColor: getValidColor(colors.backgroundBodyDecorationColor, effectiveColors.primary),
          BackgroundFooterColor: getValidColor(colors.backgroundFooterColor, effectiveColors.background),
          TextPrimaryColor: getValidColor(colors.textPrimaryColor, effectiveColors.text),
          TextSegundaryColor: getValidColor(colors.textSegundaryColor, effectiveColors.text),
          ButtonRedirectWebSiteColor: getValidColor(colors.buttonRedirectWebSiteColor, effectiveColors.background),
          TextButtonRedirectWebSiteColor: getValidColor(colors.textButtonRedirectWebSiteColor, effectiveColors.primary),
          ButtonRedirectEivoiceColor: getValidColor(colors.buttonRedirectEivoiceColor, effectiveColors.primary),
          TextButtonRedirectEivoiceColor: getValidColor(colors.textButtonRedirectEivoiceColor, effectiveColors.background),
        };

        console.log('Rendering template with colors:', { effectiveColors, advancedColors: colors });

        // Convert template
        const mustacheTemplate = convertJinja2ToMustache(templateResponse.templateHtml, templateData, effectiveColors);
        const html = Mustache.render(mustacheTemplate, templateData);

        // Sanitize HTML to remove scripts (they won't work in sandboxed iframe anyway)
        const sanitizedHtml = sanitizeHtml(html);

        console.log('Preview rendered successfully, HTML length:', html.length, 'Sanitized length:', sanitizedHtml.length);
        setPreviewHtml(sanitizedHtml);
      } catch (err) {
        console.error('Error loading preview:', err);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    const timeoutId = setTimeout(() => {
      loadPreview();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [showPreview, activeTab, colors, data?.template, previewRefreshKey]);

  // No need for separate useEffect to write to iframe - using srcdoc attribute instead

  // Helper function to sanitize HTML by removing scripts
  const sanitizeHtml = (html: string): string => {
    // Remove all script tags and their content
    let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    // Remove inline event handlers (onclick, onload, etc.)
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
    sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');
    return sanitized;
  };

  // Helper function to convert Jinja2 to Mustache (copied from EmailTemplateModal)
  // Helper function to get the first valid (non-empty) color value
  const getValidColor = (...values: (string | undefined)[]): string => {
    for (const value of values) {
      if (value && value.trim() !== '') {
        return value;
      }
    }
    return values[values.length - 1] || '#000000';
  };

  const convertJinja2ToMustache = (template: string, _data: any, effectiveColors?: { background: string; primary: string; text: string }): string => {
    let result = template;

    const colorsToUse = effectiveColors || {
      background: getValidColor(colors.colorbackground, '#dbe5ea'),
      primary: getValidColor(colors.colorprimary, '#242b3d'),
      text: getValidColor(colors.colortext, '#126de5'),
    };

    // Usar colores avanzados si están disponibles, sino usar los básicos
    // Priority: advanced color > basic color > default
    const cssVarMap: Record<string, string> = {
      'BackgroundHeaderColor': getValidColor(colors.backgroundHeaderColor, colorsToUse.primary, '#54A205'),
      'BackgroundBodyColor': getValidColor(colors.backgroundBodyColor, colorsToUse.background, '#D4D4D4'),
      'BackgroundBodyDecorationColor': getValidColor(colors.backgroundBodyDecorationColor, colorsToUse.primary, '#FFFFFF'),
      'BackgroundFooterColor': getValidColor(colors.backgroundFooterColor, colorsToUse.background, '#D4D4D4'),
      'TextPrimaryColor': getValidColor(colors.textPrimaryColor, colorsToUse.text, '#000000'),
      'TextSegundaryColor': getValidColor(colors.textSegundaryColor, colorsToUse.text, '#000000'),
      'ButtonRedirectWebSiteColor': getValidColor(colors.buttonRedirectWebSiteColor, colorsToUse.background, '#FFFFFF'),
      'TextButtonRedirectWebSiteColor': getValidColor(colors.textButtonRedirectWebSiteColor, colorsToUse.primary, '#000000'),
      'ButtonRedirectEivoiceColor': getValidColor(colors.buttonRedirectEivoiceColor, colorsToUse.primary, '#101010'),
      'TextButtonRedirectEivoiceColor': getValidColor(colors.textButtonRedirectEivoiceColor, colorsToUse.background, '#F9F9F9'),
    };

    Object.keys(cssVarMap).forEach((varName) => {
      const regex = new RegExp(`var\\(--${varName}\\s*\\)`, 'g');
      result = result.replace(regex, cssVarMap[varName]);
    });

    result = result.replace(/\{% for (\w+) in ([^\s%]+) %\}/g, '{{#$2}}');
    const forMatches = [...template.matchAll(/\{% for \w+ in ([^\s%]+) %\}/g)];
    forMatches.reverse().forEach((match) => {
      const arrayName = match[1];
      result = result.replace(/\{% endfor %\}/, `{{/${arrayName}}}`);
    });

    result = result.replace(/\{% else %\}[\s\S]*?\{% endif %\}/g, '{% endif %}');
    result = result.replace(/\{% if [^%]+ %\}/g, '');
    result = result.replace(/\{% endif %\}/g, '');
    result = result.replace(/\{% else %\}/g, '');
    result = result.replace(/\{\{#if ([^}]+)\}\}/g, '');
    result = result.replace(/\{\{\/if\}\}/g, '');

    return result;
  };

  // Load CAFE templates when component has data
  useEffect(() => {
    const loadCafeTemplates = async () => {
      try {
        setLoadingCafeTemplates(true);
        const templates = await templateService.getCafeTemplates();
        console.log('CAFE templates response:', templates);

        setCafeTemplates(Array.isArray(templates) ? templates : []);

        // Inicializar el dropdown con el template guardado en Aludra (cafeTemplate es un NAME)
        // Si no hay guardado, usar CafeTemplateDefault como fallback
        if (Array.isArray(templates)) {
          const savedTemplateName = data?.template?.cafeTemplate;
          const savedTemplate = savedTemplateName
            ? templates.find((t) => t.name === savedTemplateName)
            : null;
          const fallbackTemplate = templates.find((t) => t.name === 'CafeTemplateDefault');
          const targetTemplate = savedTemplate || fallbackTemplate;
          if (targetTemplate) {
            setSelectedCafe(targetTemplate._id);
          }
        }
      } catch (err) {
        console.error('Error loading CAFE templates:', err);
        // Set empty array on error to prevent further issues
        setCafeTemplates([]);
      } finally {
        setLoadingCafeTemplates(false);
      }
    };

    // Only load CAFE templates if we have a template loaded
    if (data?.template) {
      loadCafeTemplates();
    }
  }, [data?.template]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert variant="warning">
        Error al cargar la configuración de templates. Por favor, intente nuevamente.
      </Alert>
    );
  }

  // Show no data state
  if (!data?.template) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-8">
            <p className="text-secondary-600 dark:text-secondary-400">
              No hay configuración de template disponible para esta compañía.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const template = data.template;

  // Tab configuration - Solo para Email (CAFE estará en su propia sección)
  const tabs = [
    { id: 'general' as const, label: 'General' },
    { id: 'email' as const, label: 'Email' },
    { id: 'colors' as const, label: 'Colores' },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Templates
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            Gestiona la configuración de templates de email y comprobantes CAFE
          </p>
        </div>
        <Badge variant={template.enabled ? 'success' : 'info'} className="h-fit">
          {template.enabled ? 'Habilitado' : 'Deshabilitado'}
        </Badge>
      </div>

      {/* SECCION 1: EMAIL TEMPLATES */}
      <div>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
            Configuración de Email
          </h2>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            Personaliza el diseño y contenido de los emails enviados a tus clientes
          </p>
        </div>

        <div className={`grid gap-6 ${showPreview && activeTab === 'colors' ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
          {/* Left Side: Configuration Form */}
          <div className="space-y-6">
            {/* Tabs Navigation */}
            <Card>
              <CardBody className="p-0">
                <div className="flex border-b border-secondary-200 dark:border-secondary-700 overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        px-6 py-4 text-sm font-medium whitespace-nowrap
                        border-b-2 transition-colors duration-200
                        ${
                          activeTab === tab.id
                            ? 'border-primary-600 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-transparent text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-50 dark:hover:bg-secondary-800/50'
                        }
                      `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </CardBody>
            </Card>

          {/* Tab Content */}
          <Card>
            <CardBody>
              {/* GENERAL TAB */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Configuración General
                  </h3>

                  {/* Send Email Toggle */}
                  <div className="p-4 bg-secondary-50 dark:bg-secondary-800/30 rounded-lg border border-secondary-200 dark:border-secondary-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <label htmlFor="sendEmail" className="text-sm font-semibold text-secondary-900 dark:text-white block">
                          Envío Automático de Email
                        </label>
                        <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1.5 leading-relaxed">
                          Habilitar el envío automático de emails cuando se genere una factura electrónica
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          id="sendEmail"
                          checked={sendEmail}
                          onChange={(e) => setSendEmail(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-secondary-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-secondary-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-secondary-600 peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                      Descripción del Template
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Describe el propósito y uso de este template..."
                    />
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1.5">
                      Información adicional sobre este template para tu referencia
                    </p>
                  </div>

                  {/* URL de Acción (Read-only) */}
                  <div className="p-4 bg-primary-50 dark:bg-primary-900/10 rounded-lg border border-primary-200 dark:border-primary-800">
                    <span className="block text-xs font-semibold text-primary-900 dark:text-primary-100 mb-2 uppercase tracking-wider">
                      URL de Acción del Template
                    </span>
                    <a
                      href={template.urlAction}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline text-sm break-all font-mono"
                    >
                      {template.urlAction}
                    </a>
                  </div>
                </div>
              )}

              {/* EMAIL TAB */}
              {activeTab === 'email' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Configuración de Email
                  </h3>

                  {/* Template ID Info */}
                  <div className="p-3 bg-info-50 dark:bg-info-900/10 rounded-lg border border-info-200 dark:border-info-800">
                    <p className="text-xs font-semibold text-info-900 dark:text-info-100 mb-1">
                      SendGrid Template ID
                    </p>
                    <p className="text-xs font-mono text-info-700 dark:text-info-300 break-all">
                      {template.emailTemplate.emailTemplateId}
                    </p>
                  </div>

                  {/* Email Configuration Fields */}
                  <div className="space-y-4">
                    {/* Row 1: Email To & Title (Grid 2 cols) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                          Email Para
                        </label>
                        <input
                          type="email"
                          value={emailTo}
                          onChange={(e) => setEmailTo(e.target.value)}
                          className="w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="destinatario@ejemplo.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                          Título del Email
                        </label>
                        <input
                          type="text"
                          value={emailTitle}
                          onChange={(e) => setEmailTitle(e.target.value)}
                          className="w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Comprobante de Facturación Electrónica"
                        />
                      </div>
                    </div>

                    {/* Row 2: Email From & Subject (Grid 2 cols) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                          Email De (Remitente) <span className="text-danger-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={emailFrom}
                          onChange={(e) => setEmailFrom(e.target.value)}
                          className="w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="remitente@tuempresa.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                          Asunto del Email <span className="text-danger-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          className="w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Tu Factura Electrónica - #12345"
                        />
                      </div>
                    </div>

                    {/* Row 3: ID de Template (Read-only) */}
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                        ID de Template
                      </label>
                      <p className="px-3 py-2 font-mono text-xs text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg border border-secondary-200 dark:border-secondary-700">
                        {template.emailTemplate.emailTemplateId || 'N/A'}
                      </p>
                    </div>

                    {/* Row 4: Footer */}
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                        Footer del Email
                      </label>
                      <textarea
                        value={footer}
                        onChange={(e) => setFooter(e.target.value)}
                        rows={3}
                        className="w-full rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="© 2025 Tu Empresa. Todos los derechos reservados."
                      />
                    </div>

                    {/* Banner Toggle */}
                    <div className="p-4 bg-secondary-50 dark:bg-secondary-800/30 rounded-lg border border-secondary-200 dark:border-secondary-700">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <label htmlFor="enableBanner" className="text-sm font-semibold text-secondary-900 dark:text-white block">
                            Mostrar Banner
                          </label>
                          <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1.5 leading-relaxed">
                            Incluir un banner visual en la parte superior del email
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-4">
                          <input
                            type="checkbox"
                            id="enableBanner"
                            checked={enableBanner}
                            onChange={(e) => setEnableBanner(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-secondary-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-secondary-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-secondary-600 peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                    </div>

                  </div>
                </div>
              )}

              {/* COLORS TAB */}
              {activeTab === 'colors' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Colores del Template
                  </h3>

                  <p className="text-sm text-secondary-600 dark:text-secondary-400">
                    Personaliza los colores del template para que coincidan con tu marca
                  </p>

                  {/* Colores Principales */}
                  <div className="p-5 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/10 rounded-lg border border-primary-200 dark:border-primary-800">
                    <div className="mb-4">
                      <h4 className="text-sm font-bold text-primary-900 dark:text-primary-100">
                        Colores Principales
                      </h4>
                      <p className="text-xs text-primary-700 dark:text-primary-200 mt-1">
                        Estos colores se aplicarán a todo el email. Los colores avanzados (opcionales) permiten personalizar secciones específicas.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Color Primario */}
                      <div>
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                          Color Primario
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={colors.colorprimary || '#242b3d'}
                            onChange={(e) => handleColorChange('colorprimary', e.target.value)}
                            className="h-12 w-12 rounded-lg border-2 border-secondary-300 dark:border-secondary-600 cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                          />
                          <div className="flex-1">
                            <input
                              type="text"
                              value={colors.colorprimary || '#242b3d'}
                              onChange={(e) => handleColorChange('colorprimary', e.target.value)}
                              className="w-full px-2 py-1 text-xs font-mono bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500"
                              placeholder="#242b3d"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Color de Fondo */}
                      <div>
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                          Color de Fondo
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={colors.colorbackground || '#dbe5ea'}
                            onChange={(e) => handleColorChange('colorbackground', e.target.value)}
                            className="h-12 w-12 rounded-lg border-2 border-secondary-300 dark:border-secondary-600 cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                          />
                          <div className="flex-1">
                            <input
                              type="text"
                              value={colors.colorbackground || '#dbe5ea'}
                              onChange={(e) => handleColorChange('colorbackground', e.target.value)}
                              className="w-full px-2 py-1 text-xs font-mono bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500"
                              placeholder="#dbe5ea"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Color de Texto */}
                      <div>
                        <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-2">
                          Color de Texto
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={colors.colortext || '#126de5'}
                            onChange={(e) => handleColorChange('colortext', e.target.value)}
                            className="h-12 w-12 rounded-lg border-2 border-secondary-300 dark:border-secondary-600 cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                          />
                          <div className="flex-1">
                            <input
                              type="text"
                              value={colors.colortext || '#126de5'}
                              onChange={(e) => handleColorChange('colortext', e.target.value)}
                              className="w-full px-2 py-1 text-xs font-mono bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded focus:ring-2 focus:ring-primary-500"
                              placeholder="#126de5"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Colores Detallados (Colapsable) */}
                  <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setShowAdvancedColors(!showAdvancedColors)}
                      className="w-full p-4 bg-secondary-50 dark:bg-secondary-800/50 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                              Colores Avanzados
                            </span>
                            <span className="text-xs text-secondary-500 dark:text-secondary-400">
                              (Opcional)
                            </span>
                          </div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5 text-left">
                            Personaliza cada sección del email individualmente
                          </p>
                        </div>
                      </div>
                      <span className={`text-secondary-600 dark:text-secondary-400 transform transition-transform ${showAdvancedColors ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </button>

                    {showAdvancedColors && (
                      <div className="p-5 bg-white dark:bg-secondary-900/20">
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-xs text-secondary-600 dark:text-secondary-400">
                            Ajusta colores específicos para cada sección del template
                          </p>
                          <button
                            onClick={() => {
                              // First update the colors
                              setColors(prev => ({
                                ...prev,
                                backgroundBodyColor: '',
                                backgroundBodyDecorationColor: '',
                                backgroundFooterColor: '',
                                backgroundHeaderColor: '',
                                buttonRedirectEivoiceColor: '',
                                buttonRedirectWebSiteColor: '',
                                textButtonRedirectEivoiceColor: '',
                                textButtonRedirectWebSiteColor: '',
                                textPrimaryColor: '',
                                textSegundaryColor: '',
                              }));
                              // Then force preview refresh in the next tick
                              setTimeout(() => {
                                setPreviewRefreshKey(prev => prev + 1);
                              }, 0);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-secondary-700 dark:text-secondary-300 bg-secondary-100 dark:bg-secondary-800 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-md transition-colors"
                          >
                            Resetear Avanzados
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Background Colors */}
                <div>
                  <label className="block text-xs text-secondary-600 dark:text-secondary-400 mb-1">
                    Fondo del Cuerpo
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colors.backgroundBodyColor || '#D4D4D4'}
                      onChange={(e) => handleColorChange('backgroundBodyColor', e.target.value)}
                      className="h-10 w-16 rounded border border-secondary-300 dark:border-secondary-600 cursor-pointer"
                    />
                    <span className="text-xs font-mono text-secondary-700 dark:text-secondary-300">
                      {colors.backgroundBodyColor || '#D4D4D4'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-secondary-600 dark:text-secondary-400 mb-1">
                    Decoración del Cuerpo
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colors.backgroundBodyDecorationColor || '#FFFFFF'}
                      onChange={(e) => handleColorChange('backgroundBodyDecorationColor', e.target.value)}
                      className="h-10 w-16 rounded border border-secondary-300 dark:border-secondary-600 cursor-pointer"
                    />
                    <span className="text-xs font-mono text-secondary-700 dark:text-secondary-300">
                      {colors.backgroundBodyDecorationColor || '#FFFFFF'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-secondary-600 dark:text-secondary-400 mb-1">
                    Fondo del Header
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colors.backgroundHeaderColor || '#54A205'}
                      onChange={(e) => handleColorChange('backgroundHeaderColor', e.target.value)}
                      className="h-10 w-16 rounded border border-secondary-300 dark:border-secondary-600 cursor-pointer"
                    />
                    <span className="text-xs font-mono text-secondary-700 dark:text-secondary-300">
                      {colors.backgroundHeaderColor || '#54A205'}
                    </span>
                  </div>
                </div>

                {/* Button Colors */}
                <div>
                  <label className="block text-xs text-secondary-600 dark:text-secondary-400 mb-1">
                    Botón Ver Factura
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colors.buttonRedirectEivoiceColor || '#101010'}
                      onChange={(e) => handleColorChange('buttonRedirectEivoiceColor', e.target.value)}
                      className="h-10 w-16 rounded border border-secondary-300 dark:border-secondary-600 cursor-pointer"
                    />
                    <span className="text-xs font-mono text-secondary-700 dark:text-secondary-300">
                      {colors.buttonRedirectEivoiceColor || '#101010'}
                    </span>
                  </div>
                </div>

                          <div>
                            <label className="block text-xs font-semibold text-secondary-700 dark:text-secondary-300 mb-1">
                              Texto Botón Ver Factura
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={colors.textButtonRedirectEivoiceColor || '#F9F9F9'}
                                onChange={(e) => handleColorChange('textButtonRedirectEivoiceColor', e.target.value)}
                                className="h-10 w-16 rounded border border-secondary-300 dark:border-secondary-600 cursor-pointer"
                              />
                              <span className="text-xs font-mono text-secondary-700 dark:text-secondary-300">
                                {colors.textButtonRedirectEivoiceColor || '#F9F9F9'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Logo Upload Section */}
                  <div className="p-4 bg-primary-50 dark:bg-primary-900/10 rounded-lg border border-primary-200 dark:border-primary-800">
                    <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
                      Logo del Comprobante
                    </h4>

                    {/* Current Logo Preview */}
                    {template.emailTemplate.logo && (
                      <div className="mb-4">
                        <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-2">
                          Logo Actual
                        </p>
                        <div className="flex items-start gap-3">
                          <div className="inline-block p-3 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-300 dark:border-secondary-600">
                            <img
                              src={template.emailTemplate.logo}
                              alt="Company Logo"
                              className="max-w-[200px] h-auto object-contain"
                            />
                          </div>
                          <button
                            onClick={handleDeleteLogo}
                            disabled={isUploadingLogo}
                            className="px-3 py-2 text-xs font-medium text-danger-700 dark:text-danger-300 bg-danger-100 dark:bg-danger-900/30 hover:bg-danger-200 dark:hover:bg-danger-800/40 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Eliminar
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Upload Error */}
                    {uploadError && (
                      <div className="mb-3">
                        <Alert variant="danger">
                          {uploadError}
                        </Alert>
                      </div>
                    )}

                    {/* Upload Success */}
                    {uploadSuccess && (
                      <div className="mb-3">
                        <Alert variant="success">
                          Logo subido exitosamente
                        </Alert>
                      </div>
                    )}

                    {/* File Input */}
                    <div className="flex items-center gap-3">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={isUploadingLogo}
                          className="block w-full text-sm text-secondary-600 dark:text-secondary-400
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-lg file:border-0
                            file:text-sm file:font-semibold
                            file:bg-primary-100 dark:file:bg-primary-900/30
                            file:text-primary-700 dark:file:text-primary-300
                            hover:file:bg-primary-200 dark:hover:file:bg-primary-800/40
                            file:cursor-pointer
                            disabled:opacity-50 disabled:cursor-not-allowed
                            cursor-pointer"
                        />
                      </label>
                      {isUploadingLogo && (
                        <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm">Subiendo...</span>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
                      Formatos: JPG, PNG, GIF. Tamaño máximo: 5MB
                    </p>
                  </div>
                </div>
              )}

            </CardBody>
          </Card>

          {/* Save Button - Diseño Moderno */}
          {(
            <div className="relative">
              {/* Alertas flotantes */}
              {saveError && (
                <div className="mb-4">
                  <Alert variant="danger">
                    {saveError}
                  </Alert>
                </div>
              )}
              {saveSuccess && (
                <div className="mb-4">
                  <Alert variant="success">
                    Configuración guardada exitosamente
                  </Alert>
                </div>
              )}

              {/* Contenedor del botón con gradiente de fondo */}
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/10 p-6 border border-primary-200 dark:border-primary-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
                      Listo para guardar tus cambios?
                    </h3>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                      Los cambios se aplicarán a todos los emails enviados
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                  if (!template) return;

                  // Plan validation
                  if (isLoadingPlan) {
                    await Swal.fire({
                      title: 'Cargando...',
                      text: 'Verificando permisos del plan',
                      icon: 'info',
                      confirmButtonText: 'Entendido',
                    });
                    return;
                  }

                  if (planLimits && !canModifyTemplate('email')) {
                    const result = await Swal.fire({
                      title: 'Función no disponible',
                      text: 'La modificación del template de email no está disponible en tu plan actual.',
                      icon: 'warning',
                      showCancelButton: true,
                      confirmButtonText: 'Entendido',
                      cancelButtonText: 'Ver planes',
                      confirmButtonColor: '#3085d6',
                    });

                    if (result.dismiss === Swal.DismissReason.cancel) {
                      window.location.href = '/subscriptions/plans';
                    }
                    return;
                  }

                  try {
                    setIsSaving(true);
                    setSaveError(null);
                    setSaveSuccess(false);

                    // Construir el objeto completo del template con los cambios
                    // IMPORTANTE: No incluir cafeTemplate para no corromper GenerateCAFE
                    const { cafeTemplate: _cafeTemplate, ...templateWithoutCafe } = template;
                    const updatedTemplate = {
                      ...templateWithoutCafe,
                      sendEmail,
                      description,
                      emailTemplate: {
                        ...template.emailTemplate,
                        emailTo,
                        emailTitle,
                        enableBanner,
                        emailFrom,
                        emailSubject,
                        footer,
                        // Contenido del email (NO SE USAN - siempre null)
                        greeting: null,
                        message: null,
                        comment: null,
                        thanks: null,
                        // Colores principales (para vista previa)
                        colorbackground: colors.colorbackground,
                        colorprimary: colors.colorprimary,
                        colortext: colors.colortext,
                        // Colores detallados
                        backgroundBodyColor: colors.backgroundBodyColor,
                        backgroundBodyDecorationColor: colors.backgroundBodyDecorationColor,
                        backgroundFooterColor: colors.backgroundFooterColor,
                        backgroundHeaderColor: colors.backgroundHeaderColor,
                        buttonRedirectEivoiceColor: colors.buttonRedirectEivoiceColor,
                        buttonRedirectWebSiteColor: colors.buttonRedirectWebSiteColor,
                        textButtonRedirectEivoiceColor: colors.textButtonRedirectEivoiceColor,
                        textButtonRedirectWebSiteColor: colors.textButtonRedirectWebSiteColor,
                        textPrimaryColor: colors.textPrimaryColor,
                        textSegundaryColor: colors.textSegundaryColor,
                      },
                    };

                    console.log('💾 Saving template configuration:', updatedTemplate);
                    console.log('📤 Template ID:', template._id);

                    const response = await templateService.updateTemplate(template._id, updatedTemplate);

                    console.log('✅ Template saved successfully:', response);

                    // Invalidar el cache para recargar los datos
                    queryClient.invalidateQueries({ queryKey: ['template'] });

                    setSaveSuccess(true);

                    // Mostrar alert de éxito
                    await Swal.fire({
                      title: '¡Guardado!',
                      text: 'La configuración del template ha sido actualizada exitosamente.',
                      icon: 'success',
                      confirmButtonText: 'Entendido',
                      timer: 3000,
                    });

                    // Ocultar mensaje de éxito después de 3 segundos
                    setTimeout(() => {
                      setSaveSuccess(false);
                    }, 3000);
                  } catch (err) {
                    console.error('❌ Error saving template:', err);
                    const errorMessage = err instanceof Error
                      ? err.message
                      : 'Error al guardar la configuración';

                    setSaveError(errorMessage);

                    // Mostrar alert de error
                    await Swal.fire({
                      title: 'Error al guardar',
                      text: errorMessage,
                      icon: 'error',
                      confirmButtonText: 'Entendido',
                    });
                  } finally {
                    setIsSaving(false);
                  }
                    }}
                    disabled={isSaving}
                    className={`
                      relative px-8 py-3 rounded-lg font-semibold text-sm
                      transition-all duration-200 ease-in-out
                      ${isSaving
                        ? 'bg-primary-400 dark:bg-primary-700 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 hover:shadow-lg hover:scale-105'
                      }
                      text-white shadow-md
                      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                      disabled:opacity-70 disabled:cursor-not-allowed
                      flex items-center gap-2
                    `}
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Guardar Configuración
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Side: Live Preview - Solo en el tab de Colores */}
        {showPreview && activeTab === 'colors' && template.emailTemplate.emailTemplateId && (
          <div className="sticky top-4 self-start">
            <Card>
              <CardBody>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    Vista Previa
                    {isLoadingPreview && (
                      <span className="text-xs text-secondary-500 ml-2">Actualizando...</span>
                    )}
                  </h3>
                </div>

                {isLoadingPreview ? (
                  <div className="flex items-center justify-center h-[calc(100vh-16rem)] bg-secondary-50 dark:bg-secondary-800/30 rounded-lg border border-secondary-200 dark:border-secondary-700">
                    <div className="text-center">
                      <Spinner />
                      <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-3">
                        Cargando vista previa...
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden bg-white shadow-sm">
                    <iframe
                      title="Email Template Live Preview"
                      className="w-full h-[calc(100vh-16rem)]"
                      srcDoc={previewHtml}
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </div>
                )}

                <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-3 text-center">
                  Los cambios en colores se actualizan automáticamente
                </p>
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </div>

      {/* SECCION 2: CAFE TEMPLATES - Completamente separada */}
      <div className="pt-8 border-t-2 border-secondary-200 dark:border-secondary-700">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
            Comprobantes CAFE
          </h2>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            Gestiona la configuración y visualización de Comprobantes Auxiliares de Factura Electrónica
          </p>
        </div>

        <Card>
          <CardBody>
            {/* Tabs for CAFE section */}
            <div className="border-b border-secondary-200 dark:border-secondary-700 mb-6">
              <div className="flex gap-1">
                <button
                  onClick={() => setCafeActiveTab('view')}
                  className={`
                    px-4 py-2 text-sm font-medium rounded-t-lg transition-colors
                    ${cafeActiveTab === 'view'
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-b-2 border-primary-600 dark:border-primary-400'
                      : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-50 dark:hover:bg-secondary-800/50'
                    }
                  `}
                >
                  Visualización
                </button>
                <button
                  onClick={() => setCafeActiveTab('config')}
                  className={`
                    px-4 py-2 text-sm font-medium rounded-t-lg transition-colors
                    ${cafeActiveTab === 'config'
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-b-2 border-primary-600 dark:border-primary-400'
                      : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-50 dark:hover:bg-secondary-800/50'
                    }
                  `}
                >
                  Configuración
                </button>
              </div>
            </div>

            {/* Visualization Tab */}
            {cafeActiveTab === 'view' && (
              <div>
                <div className="p-4 bg-info-50 dark:bg-info-900/10 rounded-lg border border-info-200 dark:border-info-800 mb-4">
                  <p className="text-sm text-secondary-700 dark:text-secondary-300 leading-relaxed">
                    Los templates CAFE son documentos complementarios requeridos en Panamá para facturas electrónicas.
                  </p>
                </div>

                {loadingCafeTemplates ? (
                  <div className="flex justify-center py-12">
                    <Spinner />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 dark:text-secondary-300 mb-3">
                      Seleccionar Template
                    </label>
                    <div className="flex gap-3">
                      <Select
                        value={selectedCafe}
                        onChange={(e) => setSelectedCafe(e.target.value)}
                        options={[
                          { value: '', label: 'Seleccione un template CAFE' },
                          ...cafeTemplates.map((t) => ({
                            value: t._id,
                            label: t.displayName,
                          })),
                        ]}
                        className="flex-1"
                      />
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (selectedCafe) {
                            setIsCafeModalOpen(true);
                          }
                        }}
                        disabled={!selectedCafe}
                      >
                        Ver Template
                      </Button>
                    </div>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
                      Selecciona un template para visualizar su estructura
                    </p>

                    {/* Botón Aplicar Template - visible en la vista principal */}
                    {selectedCafe && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/10 rounded-lg border border-primary-200 dark:border-primary-800">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-secondary-900 dark:text-white">
                              Aplicar template seleccionado
                            </h4>
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-0.5">
                              Se usará <span className="font-medium">{cafeTemplates.find(t => t._id === selectedCafe)?.displayName}</span> para generar los CAFE
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              if (!template || !selectedCafe) return;

                              if (isLoadingPlan) return;
                              if (planLimits && !canModifyTemplate('cafe')) {
                                const result = await Swal.fire({
                                  title: 'Función no disponible',
                                  text: 'La modificación del template no está disponible en tu plan actual.',
                                  icon: 'warning',
                                  showCancelButton: true,
                                  confirmButtonText: 'Entendido',
                                  cancelButtonText: 'Ver planes',
                                  confirmButtonColor: '#3085d6',
                                });
                                if (result.dismiss === Swal.DismissReason.cancel) {
                                  window.location.href = '/subscriptions/plans';
                                }
                                return;
                              }

                              try {
                                setIsSaving(true);
                                setSaveError(null);

                                const { cafeTemplate: _cafeTemplate, ...templateWithoutCafe } = template;
                                const selectedCafeName = cafeTemplates.find(t => t._id === selectedCafe)?.name;
                                const updatedTemplate = {
                                  ...templateWithoutCafe,
                                  ...(selectedCafeName ? { cafeTemplate: selectedCafeName } : {}),
                                };

                                await templateService.updateTemplate(template._id, updatedTemplate);

                                setSaveSuccess(true);
                                queryClient.invalidateQueries({ queryKey: ['template'] });

                                await Swal.fire({
                                  title: 'Template Aplicado',
                                  text: `Se ha aplicado "${cafeTemplates.find(t => t._id === selectedCafe)?.displayName}" exitosamente.`,
                                  icon: 'success',
                                  confirmButtonText: 'Entendido',
                                  timer: 3000,
                                });

                                setTimeout(() => setSaveSuccess(false), 3000);
                              } catch (err) {
                                setSaveError(
                                  err instanceof Error ? err.message : 'Error al aplicar el template'
                                );
                              } finally {
                                setIsSaving(false);
                              }
                            }}
                            disabled={isSaving}
                            className={`
                              flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm
                              transition-all duration-200 ease-in-out
                              ${isSaving
                                ? 'bg-primary-400 dark:bg-primary-700 cursor-not-allowed'
                                : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 hover:shadow-lg'
                              }
                              text-white shadow-md
                            `}
                          >
                            {isSaving ? (
                              <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Aplicando...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Aplicar Template
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Configuration Tab */}
            {cafeActiveTab === 'config' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column: Signature */}
                  <div className="space-y-4">
                    <div className="p-4 bg-primary-50 dark:bg-primary-900/10 rounded-lg border border-primary-200 dark:border-primary-800">
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
                        Firma del Comprobante
                      </h4>

                      {cafeSignature ? (
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <label className="relative block cursor-pointer flex-1">
                              <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png"
                                onChange={handleSignatureUpload}
                                disabled={isUploadingSignature}
                                className="hidden"
                              />
                              <div className="relative group">
                                <div className="p-3 bg-white dark:bg-secondary-800 rounded-lg border-2 border-dashed border-primary-300 dark:border-primary-700 transition-all group-hover:border-primary-500 dark:group-hover:border-primary-500">
                                  <img
                                    src={cafeSignature}
                                    alt="Firma"
                                    className="max-w-full max-h-[80px] h-auto object-contain mx-auto"
                                  />
                                </div>
                                <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <span className="text-white text-xs font-medium">
                                    {isUploadingSignature ? 'Subiendo...' : 'Cambiar firma'}
                                  </span>
                                </div>
                              </div>
                            </label>
                            <button
                              onClick={handleDeleteSignature}
                              disabled={isUploadingSignature}
                              className="px-3 py-2 text-xs font-medium text-danger-700 dark:text-danger-300 bg-danger-100 dark:bg-danger-900/30 hover:bg-danger-200 dark:hover:bg-danger-800/40 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Eliminar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="relative block cursor-pointer">
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            onChange={handleSignatureUpload}
                            disabled={isUploadingSignature}
                            className="hidden"
                          />
                          <div className="p-6 bg-white dark:bg-secondary-800 rounded-lg border-2 border-dashed border-secondary-300 dark:border-secondary-600 hover:border-primary-400 dark:hover:border-primary-600 transition-all">
                            <div className="text-center">
                              <svg className="mx-auto h-8 w-8 text-secondary-400 dark:text-secondary-500 mb-2" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <p className="text-xs text-secondary-700 dark:text-secondary-300 font-medium mb-1">
                                {isUploadingSignature ? 'Subiendo...' : 'Subir firma'}
                              </p>
                              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                                JPG, PNG hasta 5MB
                              </p>
                            </div>
                          </div>
                        </label>
                      )}

                      {signatureUploadSuccess && (
                        <Alert variant="success" className="mt-3">
                          Firma subida exitosamente
                        </Alert>
                      )}
                      {signatureUploadError && (
                        <Alert variant="danger" className="mt-3">
                          {signatureUploadError}
                        </Alert>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Editors */}
                  <div className="space-y-4">
                    {/* Extra HTML Editor */}
                    <div className="p-4 pb-16 bg-info-50 dark:bg-info-900/10 rounded-lg border border-info-200 dark:border-info-800">
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
                        Página Extra (Términos y Condiciones)
                      </h4>
                      <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-3">
                        Contenido HTML impreso debajo del CAFE
                      </p>
                      <div className="bg-white dark:bg-secondary-800 rounded">
                        <ReactQuill
                          theme="snow"
                          value={cafeExtraHtml}
                          onChange={setCafeExtraHtml}
                          className="h-40"
                        />
                      </div>
                    </div>

                    {/* Footer Editor */}
                    <div className="p-4 pb-12 bg-warning-50 dark:bg-warning-900/10 rounded-lg border border-warning-200 dark:border-warning-800 mt-4">
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
                        Pie de Página
                      </h4>
                      <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-3">
                        HTML para el footer (max 576 chars)
                      </p>
                      <div className="bg-white dark:bg-secondary-800 rounded">
                        <ReactQuill
                          theme="snow"
                          value={cafeFooter}
                          onChange={(value) => {
                            if (value.length <= 576) {
                              setCafeFooter(value);
                            }
                          }}
                          className="h-28"
                        />
                      </div>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-2">
                        {cafeFooter.length} / 576 caracteres
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-3 pt-6 border-t border-secondary-200 dark:border-secondary-700 mt-6">
                  <button
                    onClick={async () => {
                      if (!template) return;

                      // Wait for plan data to load
                      if (isLoadingPlan) {
                        await Swal.fire({
                          title: 'Cargando...',
                          text: 'Verificando permisos del plan',
                          icon: 'info',
                          confirmButtonText: 'Entendido',
                        });
                        return;
                      }

                      if (planLimits && !canModifyTemplate('cafe')) {
                        const result = await Swal.fire({
                          title: 'Función no disponible',
                          text: 'La modificación del template CAFE no está disponible en tu plan actual.',
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonText: 'Entendido',
                          cancelButtonText: 'Ver planes',
                          confirmButtonColor: '#3085d6',
                        });

                        if (result.dismiss === Swal.DismissReason.cancel) {
                          window.location.href = '/subscriptions/plans';
                        }
                        return;
                      }

                      try {
                        setIsSaving(true);
                        setSaveError(null);

                        // Sanitizar extraHtml: si solo tiene tags vacíos de ReactQuill (ej: <p><br></p>), guardar como vacío
                        const extraTextOnly = cafeExtraHtml
                          .replace(/<br\s*\/?>/gi, '')   // quitar <br>, <br/>, <br />
                          .replace(/<[^>]*>/g, '')       // quitar todas las etiquetas HTML
                          .replace(/&nbsp;/gi, '')       // quitar &nbsp;
                          .replace(/\s+/g, '')           // quitar espacios, tabs, newlines
                          .trim();
                        const cleanExtraHtml = extraTextOnly.length > 0 ? cafeExtraHtml : '';

                        // Sanitizar footer también
                        const footerTextOnly = cafeFooter
                          .replace(/<br\s*\/?>/gi, '')
                          .replace(/<[^>]*>/g, '')
                          .replace(/&nbsp;/gi, '')
                          .replace(/\s+/g, '')
                          .trim();
                        const cleanFooter = footerTextOnly.length > 0 ? cafeFooter : '';

                        // IMPORTANTE: No incluir cafeTemplate del spread original (puede ser ObjectId o null)
                        // Solo enviar el nombre válido del template seleccionado
                        const { cafeTemplate: _cafeTemplate, ...templateWithoutCafe } = template;
                        const selectedCafeName = cafeTemplates.find(t => t._id === selectedCafe)?.name;
                        const updatedTemplate = {
                          ...templateWithoutCafe,
                          signature: cafeSignature,
                          extraHtml: cleanExtraHtml,
                          // Solo enviar footer si tiene contenido — no enviar nada si está vacío
                          // para no interferir con el footer automático del PAC que pone Aludra
                          ...(cleanFooter ? { footer: cleanFooter } : {}),
                          ...(selectedCafeName ? { cafeTemplate: selectedCafeName } : {}),
                        };

                        await templateService.updateTemplate(template._id, updatedTemplate);

                        setSaveSuccess(true);
                        queryClient.invalidateQueries({ queryKey: ['template'] });

                        setTimeout(() => {
                          setSaveSuccess(false);
                        }, 3000);
                      } catch (err) {
                        setSaveError(
                          err instanceof Error
                            ? err.message
                            : 'Error al guardar la configuración CAFE'
                        );
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                    disabled={isSaving}
                    className={`
                      flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold text-sm
                      transition-all duration-200 ease-in-out
                      ${isSaving
                        ? 'bg-primary-400 dark:bg-primary-700 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 hover:shadow-lg hover:scale-105'
                      }
                      text-white shadow-md
                    `}
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Guardar Configuración
                      </>
                    )}
                  </button>
                </div>

                {/* Success/Error Messages */}
                {saveSuccess && (
                  <Alert variant="success" className="mt-4">
                    Configuración CAFE guardada exitosamente
                  </Alert>
                )}
                {saveError && (
                  <Alert variant="danger" className="mt-4">
                    {saveError}
                  </Alert>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Titles - Configuración de Campos (oculto) */}

      {/* Email Template Modal */}
      {template.emailTemplate.emailTemplateId && (
        <EmailTemplateModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          templateId={template.emailTemplate.emailTemplateId}
          sendGridTemplateId={template.emailTemplate.emailTemplateId}
          colors={{
            background: colors.colorbackground || template.emailTemplate.colorbackground,
            primary: colors.colorprimary || template.emailTemplate.colorprimary,
            text: colors.colortext || template.emailTemplate.colortext,
          }}
          advancedColors={{
            backgroundBodyColor: colors.backgroundBodyColor,
            backgroundBodyDecorationColor: colors.backgroundBodyDecorationColor,
            backgroundFooterColor: colors.backgroundFooterColor,
            backgroundHeaderColor: colors.backgroundHeaderColor,
            buttonRedirectEivoiceColor: colors.buttonRedirectEivoiceColor,
            buttonRedirectWebSiteColor: colors.buttonRedirectWebSiteColor,
            textButtonRedirectEivoiceColor: colors.textButtonRedirectEivoiceColor,
            textButtonRedirectWebSiteColor: colors.textButtonRedirectWebSiteColor,
            textPrimaryColor: colors.textPrimaryColor,
            textSegundaryColor: colors.textSegundaryColor,
          }}
          logo={template.emailTemplate.logo}
        />
      )}

      {/* CAFE Template Modal */}
      {template && (
        <CafeTemplateModal
          isOpen={isCafeModalOpen}
          onClose={() => setIsCafeModalOpen(false)}
          template={cafeTemplates.find((t) => t._id === selectedCafe) || null}
          colors={{
            background: template.emailTemplate.colorbackground,
            primary: template.emailTemplate.colorprimary,
            text: template.emailTemplate.colortext,
          }}
          logo={template.emailTemplate.logo}
          signature={template.signature || null}
          extraHtml={template.extraHtml || null}
          footer={template.footer || null}
        />
      )}
    </div>
  );
}
